'use client';

import { createContext, useContext } from 'react';
import { useThemeStore, type ThemeMode } from '@/core/stores/theme.store';

// ---------------------------------------------------------------------------
// Context type — delegates to Zustand store under the hood
// ---------------------------------------------------------------------------
interface ThemeContextType {
  theme: ThemeMode;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

// ---------------------------------------------------------------------------
// Provider — bridges Zustand store into React Context so existing consumers
// that rely on useThemeContext() continue to work.
// ---------------------------------------------------------------------------
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const setTheme = useThemeStore((s) => s.setTheme);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook — convenience accessor via context
// ---------------------------------------------------------------------------
export function useThemeContext() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext must be used within ThemeProvider');
  return ctx;
}
